import React, { createContext, ReactNode, useContext, useState } from 'react';

import { FilesTableData } from 'src/store/files/types';

interface DroppedFileInfo {
  fileId: string;
  fileName: string;
  fileKey: string;
  path: string[];
}

interface DroppedFileContextType {
  droppedFiles: DroppedFileInfo[];
  addDroppedFile: (fileData: FilesTableData) => void;
  removeDroppedFile: (fileId: string) => void;
  clearDroppedFiles: () => void;
}

const DroppedFileContext = createContext<DroppedFileContextType | undefined>(
  undefined,
);

export const useDroppedFileContext = (): DroppedFileContextType => {
  const context = useContext(DroppedFileContext);
  if (!context) {
    throw new Error(
      'useDroppedFileContext must be used within a DroppedFileProvider',
    );
  }
  return context;
};

interface DroppedFileProviderProps {
  children: ReactNode;
}

export const DroppedFileProvider: React.FC<DroppedFileProviderProps> = ({
  children,
}) => {
  const [droppedFiles, setDroppedFiles] = useState<DroppedFileInfo[]>([]);

  const addDroppedFile = (fileData: FilesTableData) => {
    const fileInfo: DroppedFileInfo = {
      fileId: fileData.fileId,
      fileName: fileData.fileName,
      fileKey: fileData.fileKey,
      path: fileData.path,
    };

    // Check if file is already in the list to avoid duplicates
    setDroppedFiles((prev) => {
      const exists = prev.some((file) => file.fileId === fileInfo.fileId);
      if (exists) {
        return prev;
      }
      return [...prev, fileInfo];
    });
  };

  const removeDroppedFile = (fileId: string) => {
    setDroppedFiles((prev) => prev.filter((file) => file.fileId !== fileId));
  };

  const clearDroppedFiles = () => {
    setDroppedFiles([]);
  };

  const value: DroppedFileContextType = {
    droppedFiles,
    addDroppedFile,
    removeDroppedFile,
    clearDroppedFiles,
  };

  return (
    <DroppedFileContext.Provider value={value}>
      {children}
    </DroppedFileContext.Provider>
  );
};
